import { Files, General } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Stage } from './StageButton';
import StageIcon from './StageIcon';
import type { Size, UpdateStatus } from './types';

export type StageFilterSelectProps = {
  stages: Stage[];
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Show the label text. Default true; set false for an icon-only trigger.
   *  Forwarded to FilterSelect. */
  showText?: boolean;
  /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
  status?: UpdateStatus;
  includeRemoveOption?: boolean;
  size?: Size;
  color?: ColorName;
  tone?: Style;
  /** Render with/without the field border. ButtonGroup sets this to `false`
   *  (and `shape='square'`) when joining triggers; forwarded to FilterSelect. */
  bordered?: boolean;
  /** Corner shape. ButtonGroup sets `'square'` so the group rounds the ends. */
  shape?: 'rounded' | 'pill' | 'square';
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function StageFilterSelect({
  stages,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select stage',
  className,
  showText,
  status,
  includeRemoveOption,
  size,
  color,
  tone,
  bordered,
  shape,
  onClear,
}: StageFilterSelectProps) {
  const selectedStage = stages.find((s) => s.id === value);
  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const stageOptions = stages.map((stage) => ({
    value: stage.id,
    label: stage.name || '',
    icon: <StageIcon color={stage.color} order={stage.order} size={size} />,
  }));

  const options = includeRemoveOption
    ? [{ value: '', label: 'Remove from stage', icon: <General.Trash01 />, color: 'red' as const }, ...stageOptions]
    : stageOptions;

  const select = (
    <FilterSelect
      icon={selectedStage ? <StageIcon color={selectedStage.color} order={selectedStage.order} size={size} /> : <Files.StickerCircle />}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      showText={showText}
      status={status}
      autoWidth
      size={size}
      color={color}
      tone={tone}
      bordered={bordered}
      shape={shape}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
